import { useUploadFile } from '@/hooks/useUploadFile'
import { fileBaseName, type ImportFileType } from '@/pages/library/components/import-file'
import { uploadLibraryFile } from '@/pages/library/components/upload-library-file'
import type { ChatAttachedFile, ChatAttachment } from '@/services/api/domains/chatbot.api'
import { exceedsAttachmentBudget } from '@/services/assistant/attachments/attachment-budget'
import {
  MAX_ATTACHED_FILE_TITLE_CHARS,
  MAX_ATTACHMENT_DATA_URL_CHARS,
  MAX_ATTACHMENTS,
  MAX_DOCUMENT_NAME_CHARS,
  type AttachRole,
} from '@/services/assistant/attachments/attachment-limits'
import { classifyAttachment } from '@/services/assistant/attachments/classify-attachment'
import { prepareImage, readFileAsDataUrl } from '@/services/assistant/attachments/prepare-image'
import { announceLibraryChange } from '@/services/library/library-refresh'
import { useCallback, useMemo, useRef, useState } from 'react'

import { payloadFromChips, sendBlockReason, type AttachmentChip } from '@/services/assistant/attachments/attachment-chip'

export interface AssistantAttachments {
  chips: AttachmentChip[]
  /** A note about the last pick or send (too many files, too large together), or null. */
  notice: string | null
  /** Why the message cannot be sent yet, or null. */
  blockReason: string | null
  addFiles: (files: readonly File[]) => void
  remove: (id: string) => void
  /**
   * The ready chips' payload, or null (with `notice` set) when it would exceed the count or size budget.
   * `image` is a still the same turn carries in the legacy `image` field: the server counts it too.
   */
  takeForSend: (image?: string | null) => { attachments: ChatAttachment[]; attachedFiles: ChatAttachedFile[] } | null
  /** Drops every chip, cancelling any upload still on its way (an identity change must never finish one). */
  clear: () => void
  /** Puts back the ready chips a failed send carried, so nobody has to attach them again. */
  restore: (chips: readonly AttachmentChip[]) => void
}

let nextChipId = 0

/**
 * The assistant composer's attachments: routes each file by kind, prepares pictures and PDFs in the
 * browser, and uploads data files to the Library one at a time (the upload hook carries one upload, so a
 * second file waits as `queued`). `role` decides what may be attached — see `classifyAttachment`.
 */
export function useAssistantAttachments(role: AttachRole): AssistantAttachments {
  const [chips, setChips] = useState<AttachmentChip[]>([])
  const [notice, setNotice] = useState<string | null>(null)
  const upload = useUploadFile()
  const uploadRef = useRef(upload)
  uploadRef.current = upload
  const chipsRef = useRef(chips)
  chipsRef.current = chips
  const removedRef = useRef(new Set<string>())
  const queueRef = useRef<Promise<void>>(Promise.resolve())
  // One per data-file chip, queued or uploading: aborting it stops the File record being created.
  const cancelRef = useRef(new Map<string, AbortController>())

  const patch = useCallback((id: string, change: Partial<AttachmentChip>) => {
    if (removedRef.current.has(id)) return
    setChips(prev => prev.map(c => (c.id === id ? { ...c, ...change } : c)))
  }, [])

  const prepare = useCallback(
    async (id: string, file: File, kind: 'image' | 'document') => {
      try {
        const dataUrl = kind === 'image' ? await prepareImage(file) : await readFileAsDataUrl(file)

        if (dataUrl.length > MAX_ATTACHMENT_DATA_URL_CHARS) {
          patch(id, { status: 'refused', reason: 'Too large to send' })

          return
        }

        const attachment: ChatAttachment =
          kind === 'image'
            ? { kind: 'image', dataUrl }
            : { kind: 'document', name: file.name.slice(0, MAX_DOCUMENT_NAME_CHARS), dataUrl }
        patch(id, { status: 'ready', attachment, ...(kind === 'image' ? { previewUrl: dataUrl } : {}) })
      } catch (err) {
        patch(id, { status: 'failed', reason: err instanceof Error ? err.message : 'Could not read the file' })
      }
    },
    [patch],
  )

  const enqueueUpload = useCallback(
    (id: string, file: File, fileType: ImportFileType) => {
      const cancel = new AbortController()
      cancelRef.current.set(id, cancel)
      queueRef.current = queueRef.current.then(async () => {
        if (removedRef.current.has(id) || cancel.signal.aborted) return
        patch(id, { status: 'uploading' })

        try {
          const args = { file, fileType, title: fileBaseName(file.name) }
          const doc = await uploadLibraryFile(uploadRef.current, args, cancel.signal)
          // The file is in the Library whether or not the chip survived, so the Library hears of it.
          announceLibraryChange({ changedIds: [doc._id], deletedIds: [] })
          patch(id, {
            status: 'ready',
            attachedFile: { id: doc._id, title: doc.title.slice(0, MAX_ATTACHED_FILE_TITLE_CHARS), fileType: doc.fileType },
          })
        } catch (err) {
          patch(id, { status: 'failed', reason: err instanceof Error ? err.message : 'Upload failed' })
        } finally {
          cancelRef.current.delete(id)
          uploadRef.current.reset()
        }
      })
    },
    [patch],
  )

  const addFiles = useCallback(
    (files: readonly File[]) => {
      const room = MAX_ATTACHMENTS - chipsRef.current.length
      const taken = files.slice(0, Math.max(0, room))
      const left = files.length - taken.length
      setNotice(left > 0 ? `Up to ${MAX_ATTACHMENTS} attachments per message — ${left} not added` : null)

      const added: AttachmentChip[] = []
      const work: Array<() => void> = []

      for (const file of taken) {
        const id = `att-${++nextChipId}`
        const route = classifyAttachment(file, role)

        if (route.kind === 'refused') {
          added.push({ id, name: file.name, kind: 'other', status: 'refused', reason: route.reason })
        } else if (route.kind === 'data') {
          added.push({ id, name: file.name, kind: 'data', status: 'queued', dataFileType: route.fileType })
          work.push(() => enqueueUpload(id, file, route.fileType))
        } else {
          added.push({ id, name: file.name, kind: route.kind, status: 'preparing' })
          work.push(() => void prepare(id, file, route.kind))
        }
      }

      if (added.length === 0) return
      chipsRef.current = [...chipsRef.current, ...added]
      setChips(prev => [...prev, ...added])
      for (const run of work) run()
    },
    [enqueueUpload, prepare, role],
  )

  /** Marks chips gone and cancels their uploads; the upload hook carries one upload, so abort it once. */
  const drop = useCallback((ids: readonly string[]) => {
    let uploading = false

    for (const id of ids) {
      removedRef.current.add(id)
      cancelRef.current.get(id)?.abort()
      cancelRef.current.delete(id)
      if (chipsRef.current.find(c => c.id === id)?.status === 'uploading') uploading = true
    }

    if (uploading) void uploadRef.current.abort()
    chipsRef.current = chipsRef.current.filter(c => !ids.includes(c.id))
    setChips(prev => prev.filter(c => !ids.includes(c.id)))
    setNotice(null)
  }, [])

  const remove = useCallback((id: string) => drop([id]), [drop])

  const clear = useCallback(() => drop(chipsRef.current.map(c => c.id)), [drop])

  const restore = useCallback((sent: readonly AttachmentChip[]) => {
    const back = sent
      .filter(c => c.status === 'ready' && !chipsRef.current.some(k => k.id === c.id))
      .slice(0, Math.max(0, MAX_ATTACHMENTS - chipsRef.current.length))

    if (back.length === 0) return
    for (const c of back) removedRef.current.delete(c.id)
    chipsRef.current = [...back, ...chipsRef.current]
    setChips(prev => [...back, ...prev])
  }, [])

  const takeForSend = useCallback((image?: string | null) => {
    const payload = payloadFromChips(chipsRef.current)

    // A still riding the same turn (`image`) counts toward both the five and the size budget.
    if (payload.attachments.length + (image ? 1 : 0) > MAX_ATTACHMENTS) {
      setNotice(`Up to ${MAX_ATTACHMENTS} pictures and PDFs per message, the attached still included`)

      return null
    }

    if (exceedsAttachmentBudget(payload.attachments, image)) {
      setNotice('These attachments are too large to send together — remove one and try again')

      return null
    }

    return payload
  }, [])

  const percent = upload.percent

  return useMemo(
    () => ({
      chips: chips.map(c => (c.status === 'uploading' ? { ...c, progress: percent } : c)),
      notice,
      blockReason: sendBlockReason(chips),
      addFiles,
      remove,
      takeForSend,
      clear,
      restore,
    }),
    [chips, percent, notice, addFiles, remove, takeForSend, clear, restore],
  )
}
